import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ReportCover from "@/components/marketing/ReportCover";

const FLAG = "connexis_exit_capture_done";

export default function ExitCapture() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    if (window.self !== window.top) return;
    if (localStorage.getItem(FLAG) === "1") return;
    const armTimer = setTimeout(() => { armed.current = true; }, 8000);
    const onLeave = (e) => {
      if (!armed.current || e.clientY > 0) return;
      if (window.location.pathname.startsWith("/benchmark-report")) return;
      if (localStorage.getItem(FLAG) === "1") return;
      localStorage.setItem(FLAG, "1");
      setOpen(true);
    };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(armTimer); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Add your work email first"); return; }
    setSending(true);
    try {
      const res = await api.post("/reports/benchmark", { email }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Connexis-CPL-Benchmark-Report-2026.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(true);
      setTimeout(() => setOpen(false), 3200);
    } catch (_) {
      toast.error("Could not send the report — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border-0" data-testid="exit-capture-modal">
        <div className="bg-[#0B1220] px-7 pt-7 pb-6">
          <div className="flex items-start gap-4">
            <ReportCover scale={0.55} tilt={-3} testId="exit-capture-cover" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#60A5FA]">Before you go</div>
              <h3 className="mt-1.5 text-[19px] font-semibold tracking-tight text-white leading-snug">
                Take the 2026 CPL Benchmark Report with you.
              </h3>
            </div>
          </div>
          <p className="mt-4 text-[13px] text-slate-400 leading-relaxed">
            Sector lead prices, close-rate ranges and implied acquisition costs — a free 5-page PDF, no sales call.
          </p>
        </div>
        <div className="px-7 py-6 bg-white">
          {done ? (
            <div className="text-center py-2" data-testid="exit-capture-success">
              <Download className="size-6 text-emerald-600 mx-auto" />
              <p className="mt-2 text-sm font-medium text-slate-900">Downloading now — a copy is in your inbox too.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Input
                type="email"
                data-testid="exit-capture-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
                className="h-11"
              />
              <Button type="submit" disabled={sending} data-testid="exit-capture-submit" className="w-full rounded-full h-11 bg-[#0055FF] hover:bg-[#0044CC]">
                {sending ? <Loader2 className="size-4 animate-spin" /> : <>Send me the report <ArrowUpRight className="size-4" /></>}
              </Button>
              <button type="button" onClick={() => setOpen(false)} className="w-full text-center text-[12px] text-slate-400 hover:text-slate-600 transition-colors" data-testid="exit-capture-dismiss">
                No thanks, I'll pass
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
